import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover } from 'antd';
import Tooltip from '../../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { mutateInjectVariablesInDocument } from '../../../utils/variables'
import { mutateSearchAndLaunchTrigger } from '../../../utils/triggers'
import { getActiveField } from '../../../utils/activeFields'

// Bouton affiché dans l'aperçu du formulaire, à côté du bouton d'information,
// dès que la variable texte custom possède des valeurs prédéfinies (configurées
// dans le formulaire, onglet de gauche). Un clic sur une valeur l'insère à la
// position du curseur dans le champ.
const PredefinedValuesPicker = ({ variableKey, zoom }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const predefinedValues = useStore(({ variables }) => variables[variableKey]?.predefinedValues)
  const updateVariable = useStore(({ updateVariable }) => updateVariable)

  const options = useMemo(
    () => (predefinedValues ?? []).filter((value) => typeof value === 'string' && value.trim() !== ''),
    [predefinedValues]
  )

  // L'aperçu est une liste virtualisée : en défilant, la ligne peut être
  // démontée et le popover resterait à flotter loin de son bouton. En capture,
  // le défilement d'un conteneur interne ne remontant pas jusqu'à window.
  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    window.addEventListener('scroll', close, true)
    return () => window.removeEventListener('scroll', close, true)
  }, [open])

  // Le popover reste ouvert : plusieurs valeurs peuvent s'enchaîner, et le
  // curseur regagne le champ après chaque insertion. Le fermer rendrait le
  // focus au bouton et se battrait avec cette reprise du curseur.
  const handleApply = useCallback((value) => {
    // Champ à l'écran : insertion à la position du curseur, sans retour à la
    // ligne — l'utilisateur place son curseur là où il veut la valeur.
    const field = getActiveField(variableKey)
    if (field?.insertAtCursor) {
      field.insertAtCursor(value)
      return
    }

    // Repli quand le champ n'est pas monté (ligne du formulaire non rendue par
    // la liste virtualisée) : ajout en fin de valeur.
    // Lecture à la volée : évite d'abonner le composant à tout l'objet variables.
    const { variables } = useStore.getState()
    const currentValue = String(variables[variableKey]?.value ?? '')

    updateVariable(variableKey, (variable, draft) => {
      variable.value = `${currentValue}${value}`
      mutateSearchAndLaunchTrigger({ triggerId: variableKey, draft })
      mutateInjectVariablesInDocument({
        variables,
        variable,
        variableKey,
        draft
      })
    })
  }, [updateVariable, variableKey])

  if (options.length === 0) return null

  // stopPropagation : le contenu est porté par un portail mais les évènements
  // React remontent l'arbre des composants, jusqu'aux poignées de sélection de
  // la ligne du formulaire.
  // Un clic applique : pas de bouton de validation, la valeur part directement
  // dans le champ.
  const content = (
    <div
      style={{ minWidth: 200, maxWidth: 320, maxHeight: 260, overflowY: 'auto' }}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((value) => (
        <button key={value} type="button" onClick={() => handleApply(value)} style={valueButtonStyle}>
          {value}
        </button>
      ))}
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger='click'
      placement='rightTop'
      title={t('apply-predefined-values')}
      content={content}
      overlayStyle={{ zoom: `${zoom ?? 100}%` }}
    >
      {/* span porteur du ref : FontAwesomeIcon rend un <svg> et le Tooltip
          imbriqué réclame déjà son propre enfant. */}
      <span
        style={{ display: 'inline-flex' }}
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip title={t('apply-predefined-values')} mouseLeaveDelay="0">
          <FontAwesomeIcon
            icon={icon({ name: 'list-check', style: 'solid' })}
            style={{ marginLeft: 6, color: '#5499DE', cursor: 'pointer' }}
          />
        </Tooltip>
      </span>
    </Popover>
  )
}

const valueButtonStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '6px 8px',
  border: 'none',
  borderRadius: 4,
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 13,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

export default PredefinedValuesPicker
